import { useState, useEffect } from "react";
import { useAuth, API, BACKEND_URL } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Menu, FileDown, Tag, FolderOpen, ListChecks, Search, Loader2, PenLine, Share2, Mail, MessageCircle, ImageIcon, Save } from "lucide-react";

export default function AdminExports() {
  const { authAxios, user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState("custom");

  // Category export state
  const [selectedCats, setSelectedCats] = useState([]);
  const [catIncludePrices, setCatIncludePrices] = useState(true);
  const [catIncludeImages, setCatIncludeImages] = useState(false);
  const [catSearch, setCatSearch] = useState("");

  // Selection export state
  const [selectedProds, setSelectedProds] = useState([]);
  const [selIncludePrices, setSelIncludePrices] = useState(true);
  const [selIncludeImages, setSelIncludeImages] = useState(false);
  const [prodSearch, setProdSearch] = useState("");

  // Custom price export state
  const [customItems, setCustomItems] = useState({});
  const [customSearch, setCustomSearch] = useState("");
  const [clientName, setClientName] = useState("");
  const [customIncludeImages, setCustomIncludeImages] = useState(false);

  // Quotazioni salvate
  const [quotations, setQuotations] = useState([]);
  const [quoteName, setQuoteName] = useState("");
  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);

  // Promo export state
  const [promoIncludeImages, setPromoIncludeImages] = useState(false);

  // Export loading
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);

  const sharePdf = async (exportType, body, via) => {
    setSharing(true);
    // Open the target window/intent IMMEDIATELY within the user gesture.
    // iOS Safari (and most PWAs) block window.open / location changes that
    // happen after an awaited promise — the gesture context is lost. We open
    // a placeholder first, then redirect it once we have the share URL.
    let popup = null;
    try {
      popup = window.open("", "_blank");
    } catch {
      popup = null;
    }
    try {
      const res = await authAxios.post("/export/share-pdf", { export_type: exportType, ...body });
      const fullUrl = `${BACKEND_URL}${res.data.share_url}`;

      let target = "";
      if (via === "whatsapp") {
        const text = encodeURIComponent(`Triolo.Store - Scarica il PDF: ${fullUrl}`);
        target = `https://wa.me/?text=${text}`;
      } else if (via === "email") {
        const subject = encodeURIComponent("Triolo.Store - Listino PDF");
        const emailBody = encodeURIComponent(`Buongiorno,\n\nEcco il link per scaricare il PDF:\n${fullUrl}\n\nCordiali saluti,\nTriolo.Store`);
        target = `mailto:?subject=${subject}&body=${emailBody}`;
      }

      // Prefer the native Web Share sheet on mobile (iOS/Android) — it bypasses
      // the popup blocker because it's a native UI invoked from the gesture.
      if (typeof navigator !== "undefined" && navigator.share && via !== "email") {
        try {
          if (popup && !popup.closed) popup.close();
          await navigator.share({
            title: "Triolo.Store - PDF",
            text: "Triolo.Store - Scarica il PDF",
            url: fullUrl,
          });
          toast.success("Link condiviso!");
          return;
        } catch {
          // User cancelled or share unsupported → fall through to wa.me/mailto
        }
      }

      if (popup && !popup.closed) {
        popup.location.href = target;
      } else {
        // Fallback: same-tab navigation (works even when popups are blocked).
        window.location.href = target;
      }
      toast.success("Link PDF generato!");
    } catch (err) {
      if (popup && !popup.closed) {
        try { popup.close(); } catch { /* ignore */ }
      }
      toast.error("Errore generazione link");
    }
    setSharing(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          authAxios.get("/categories"),
          authAxios.get("/products"),
        ]);
        setCategories(catRes.data.filter(c => c.product_count > 0).sort((a, b) => (a.code || "").localeCompare(b.code || "")));
        setProducts(prodRes.data.sort((a, b) => (a.category_code || "").localeCompare(b.category_code || "")));
      } catch { toast.error("Errore caricamento dati"); }
      setLoading(false);
    };
    fetchData();
  }, [authAxios]);

  const downloadPdf = async (url, body, filename) => {
    setExporting(true);
    try {
      const res = await authAxios.post(url, body, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("PDF scaricato!");
    } catch (err) {
      const msg = err.response?.status === 404 ? "Nessun prodotto trovato" : "Errore esportazione";
      toast.error(msg);
    }
    setExporting(false);
  };

  const toggleCat = (id) => {
    setSelectedCats(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const toggleProd = (id) => {
    setSelectedProds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const filteredCats = categories.filter(c =>
    !catSearch || c.name.toLowerCase().includes(catSearch.toLowerCase()) || (c.code || "").toLowerCase().includes(catSearch.toLowerCase())
  );

  const filteredProds = products.filter(p =>
    !prodSearch || p.name.toLowerCase().includes(prodSearch.toLowerCase()) || p.sku.toLowerCase().includes(prodSearch.toLowerCase())
  );

  const filteredCustomProds = products.filter(p =>
    !customSearch || p.name.toLowerCase().includes(customSearch.toLowerCase()) || p.sku.toLowerCase().includes(customSearch.toLowerCase())
  );

  const toggleCustomItem = (prod) => {
    setCustomItems(prev => {
      const next = { ...prev };
      if (next[prod.id]) {
        delete next[prod.id];
      } else {
        const p = (prod.price_min || prod.price_list || 0).toFixed(2);
        next[prod.id] = { price: p, display: formatPriceIT(p) };
      }
      return next;
    });
  };

  const updateCustomPrice = (prodId, price, display) => {
    setCustomItems(prev => ({
      ...prev,
      [prodId]: { ...prev[prodId], price, ...(display !== undefined && { display }) }
    }));
  };

  // Italian price formatting: "1.50" → "1,50", "3" → "3,00"
  const applyQuickPrice = (prod, value) => {
    const fixed = (parseFloat(value) || 0).toFixed(2);
    setCustomItems(prev => ({ ...prev, [prod.id]: { price: fixed, display: formatPriceIT(fixed) } }));
  };
  const formatPriceIT = (val) => {
    const num = parseFloat(String(val).replace(",", "."));
    if (isNaN(num)) return "0,00";
    return num.toFixed(2).replace(".", ",");
  };

  // Parse Italian price input: allows comma, replaces with dot internally
  const parsePriceInput = (val) => {
    return val.replace(/[^0-9.,]/g, "").replace(",", ".");
  };

  const customItemCount = Object.keys(customItems).length;

  const loadQuotations = async () => {
    try {
      const r = await authAxios.get("/quotations");
      setQuotations(r.data.items || []);
    } catch { toast.error("Errore caricamento quotazioni"); }
  };

  useEffect(() => { loadQuotations(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const quotePayload = () => ({
    name: quoteName.trim(),
    client_name: clientName,
    include_images: customIncludeImages,
    items: Object.entries(customItems).map(([product_id, data]) => {
      const prod = products.find(p => p.id === product_id);
      return {
        product_id,
        custom_price: parseFloat(data.price) || 0,
        product_name: prod?.name || null,
        product_sku: prod?.sku || null,
      };
    }),
  });

  const saveQuotation = async (asNew = false) => {
    if (!quoteName.trim()) { toast.error("Indica un nominativo per la quotazione"); return; }
    if (customItemCount === 0) { toast.error("Seleziona almeno un articolo"); return; }
    setSavingQuote(true);
    try {
      if (currentQuoteId && !asNew) {
        await authAxios.put(`/quotations/${currentQuoteId}`, quotePayload());
        toast.success("Quotazione aggiornata");
      } else {
        const r = await authAxios.post("/quotations", quotePayload());
        setCurrentQuoteId(r.data.id);
        toast.success("Quotazione salvata");
      }
      await loadQuotations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio quotazione");
    }
    setSavingQuote(false);
  };

  const openQuotation = (q) => {
    const next = {};
    (q.items || []).forEach(it => {
      const price = (it.custom_price || 0).toFixed(2);
      next[it.product_id] = { price, display: formatPriceIT(price) };
    });
    setCustomItems(next);
    setClientName(q.client_name || "");
    setQuoteName(q.name || "");
    setCurrentQuoteId(q.id);
    setCustomIncludeImages(!!q.include_images);
    setShowQuotes(false);
    toast.success(`Quotazione "${q.name}" caricata`);
  };

  const deleteQuotation = async (q) => {
    if (!window.confirm(`Eliminare la quotazione "${q.name}"?`)) return;
    try {
      await authAxios.delete(`/quotations/${q.id}`);
      if (currentQuoteId === q.id) setCurrentQuoteId(null);
      await loadQuotations();
    } catch { toast.error("Errore eliminazione"); }
  };

  const newQuotation = () => {
    setCustomItems({});
    setQuoteName("");
    setClientName("");
    setCurrentQuoteId(null);
  };

  const promoCount = products.filter(p => p.price_promo && p.price_promo > 0 && !p.non_disponibile).length;

  const ShareButtons = ({ exportType, body, disabled }) => (
    <div className="flex items-center gap-2">
      <Button
        variant="outline" size="sm"
        disabled={disabled || sharing}
        onClick={() => sharePdf(exportType, body, "whatsapp")}
        className="border-green-500/30 text-green-400 hover:text-green-300 hover:bg-green-500/10 px-2 sm:px-3"
        data-testid={`share-whatsapp-${exportType}`}
      >
        <MessageCircle className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">WhatsApp</span>
      </Button>
      <Button
        variant="outline" size="sm"
        disabled={disabled || sharing}
        onClick={() => sharePdf(exportType, body, "email")}
        className="border-blue-500/30 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 px-2 sm:px-3"
        data-testid={`share-email-${exportType}`}
      >
        <Mail className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Email</span>
      </Button>
    </div>
  );

  const allTabs = [
    { id: "custom", label: "Prezzi Personalizzati", icon: PenLine, roles: ["admin", "agent"] },
    { id: "promo", label: "Promozioni", icon: Tag, roles: ["admin", "agent"] },
    { id: "category", label: "Per Categoria", icon: FolderOpen, roles: ["admin", "agent"] },
    { id: "selection", label: "Selezione", icon: ListChecks, roles: ["admin", "agent"] },
  ];

  const tabs = allTabs.filter(t => t.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-[#09090b] flex" data-testid="admin-exports">
      <main className="flex-1 min-w-0 p-3 sm:p-6 lg:p-8 max-w-6xl">
        <PageHeader title="Esportazioni" />
        <div className="flex items-center gap-2 mb-4">
          <h1 className="font-heading text-lg sm:text-2xl font-bold text-white">Esportazioni PDF</h1>
        </div>

        {/* Tabs - grid 2x2 on mobile */}
        <div className="grid grid-cols-2 sm:flex gap-2 mb-4 border-b border-white/10 pb-3">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`export-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center sm:justify-start gap-1.5 px-2.5 py-2 rounded-lg text-[11px] sm:text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
        ) : (
          <>
            {/* TAB: Prezzi Personalizzati */}
            {activeTab === "custom" && (
              <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-3 sm:p-5">
                  <div className="flex flex-col sm:flex-row gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                      <Input
                        placeholder="Cerca prodotto..."
                        value={customSearch}
                        onChange={(e) => setCustomSearch(e.target.value)}
                        className="pl-8 h-8 text-xs bg-[#27272a] border-white/10 text-white"
                        data-testid="custom-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Nome cliente..."
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        className="h-8 text-xs bg-[#27272a] border-white/10 text-white sm:w-36"
                        data-testid="client-name-input"
                      />
                      <label className="flex items-center gap-1 cursor-pointer shrink-0">
                        <Checkbox
                          checked={customIncludeImages}
                          onCheckedChange={setCustomIncludeImages}
                          data-testid="custom-include-images"
                          className="w-3.5 h-3.5"
                        />
                        <ImageIcon className="w-3 h-3 text-zinc-400" />
                      </label>
                    </div>
                  </div>

                  {customItemCount > 0 && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded px-2.5 py-1.5 mb-2 flex items-center justify-between">
                      <span className="text-xs text-emerald-400">{customItemCount} selezionati</span>
                      <button className="text-[10px] text-emerald-400 hover:text-emerald-300 underline" onClick={() => setCustomItems({})}>
                        Rimuovi
                      </button>
                    </div>
                  )}

                  {/* Quotazioni salvate */}
                  <div className="mb-3 p-2.5 rounded-lg border border-white/10 bg-[#0f0f11] max-w-lg">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={quoteName}
                        onChange={(e) => setQuoteName(e.target.value)}
                        placeholder="Nominativo quotazione"
                        data-testid="quote-name-input"
                        className="h-8 text-xs flex-1 min-w-[130px] bg-[#27272a] border-white/10 text-white"
                      />
                      <Button
                        size="sm"
                        onClick={() => saveQuotation(false)}
                        disabled={savingQuote || customItemCount === 0}
                        data-testid="quote-save-btn"
                        className="h-8 text-xs bg-sky-500 hover:bg-sky-600 text-white"
                      >
                        <Save className="w-3.5 h-3.5 mr-1" />
                        {currentQuoteId ? "Aggiorna" : "Salva"}
                      </Button>
                      {currentQuoteId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => saveQuotation(true)}
                          disabled={savingQuote}
                          data-testid="quote-saveas-btn"
                          className="h-8 text-xs border-white/10 text-zinc-300"
                        >
                          Salva come nuova
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowQuotes(v => !v)}
                        data-testid="quote-list-toggle"
                        className="h-8 text-xs border-white/10 text-zinc-300"
                      >
                        <FolderOpen className="w-3.5 h-3.5 mr-1" /> Salvate ({quotations.length})
                      </Button>
                      {(currentQuoteId || customItemCount > 0) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={newQuotation}
                          data-testid="quote-new-btn"
                          className="h-8 text-xs text-zinc-400 hover:text-white"
                        >
                          Nuova
                        </Button>
                      )}
                    </div>
                    {currentQuoteId && (
                      <p className="text-[10px] text-sky-400 mt-1.5">
                        Stai modificando una quotazione salvata: "Aggiorna" sovrascrive, "Salva come nuova" crea una copia.
                      </p>
                    )}
                    {showQuotes && (
                      <div className="mt-2 max-h-52 overflow-y-auto divide-y divide-white/5 border-t border-white/10" data-testid="quote-list">
                        {quotations.map(q => (
                          <div key={q.id} className="flex items-center gap-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => openQuotation(q)}
                              data-testid={`quote-open-${q.id}`}
                              className="flex-1 min-w-0 text-left hover:text-sky-300"
                            >
                              <span className="block text-xs text-zinc-200 truncate">{q.name}</span>
                              <span className="block text-[10px] text-zinc-500 truncate">
                                {(q.items || []).length} articoli
                                {q.client_name ? ` · ${q.client_name}` : ""}
                                {` · ${new Date(q.updated_at).toLocaleDateString("it-IT")}`}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteQuotation(q)}
                              data-testid={`quote-delete-${q.id}`}
                              className="text-[10px] text-zinc-500 hover:text-red-400 px-1"
                            >
                              Elimina
                            </button>
                          </div>
                        ))}
                        {!quotations.length && (
                          <p className="text-[10px] text-zinc-500 py-2">Nessuna quotazione salvata.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-[10px] text-zinc-500 mb-2">
                    Tocca <span className="text-sky-300 font-mono">prezzo blu</span> = minimo · <span className="text-amber-300 font-mono">prezzo giallo</span> = da autorizzare, per applicarlo subito.
                  </p>

                  <div className="max-h-[60vh] overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5 max-w-lg">
                    {filteredCustomProds.map(prod => {
                      const isSelected = !!customItems[prod.id];
                      const defaultPrice = parseFloat((prod.price_min || prod.price_list || 0).toFixed(2));
                      return (
                        <div key={prod.id} className={`flex items-center px-2 py-1.5 gap-1 ${isSelected ? "bg-emerald-500/5" : "hover:bg-white/5"}`}
                          data-testid={`custom-row-${prod.sku}`}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleCustomItem(prod)}
                            className="w-3.5 h-3.5 shrink-0"
                          />
                          <span className="text-xs text-zinc-200 truncate leading-tight min-w-0 flex-1 max-w-[50%] sm:max-w-[65%]">{prod.name}</span>
                          {prod.price_min > 0 && (
                            <button
                              type="button"
                              title="Applica prezzo minimo"
                              data-testid={`custom-min-${prod.sku}`}
                              onClick={() => applyQuickPrice(prod, prod.price_min)}
                              className="shrink-0 px-1.5 h-7 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 text-[10px] font-mono hover:bg-sky-500/20 active:scale-95 transition-colors"
                            >
                              {formatPriceIT(prod.price_min)}
                            </button>
                          )}
                          {prod.price_auth > 0 && (
                            <button
                              type="button"
                              title="Applica prezzo da autorizzare"
                              data-testid={`custom-auth-${prod.sku}`}
                              onClick={() => applyQuickPrice(prod, prod.price_auth)}
                              className="shrink-0 px-1.5 h-7 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[10px] font-mono hover:bg-amber-500/20 active:scale-95 transition-colors"
                            >
                              {formatPriceIT(prod.price_auth)}
                            </button>
                          )}
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={isSelected ? (customItems[prod.id]?.display ?? formatPriceIT(customItems[prod.id]?.price ?? "")) : formatPriceIT(defaultPrice)}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const parsed = parsePriceInput(raw);
                              if (!isSelected) {
                                setCustomItems(prev => ({ ...prev, [prod.id]: { price: parsed, display: raw } }));
                              } else {
                                updateCustomPrice(prod.id, parsed, raw);
                              }
                            }}
                            onFocus={(e) => {
                              if (!isSelected) {
                                setCustomItems(prev => ({ ...prev, [prod.id]: { price: String(defaultPrice), display: formatPriceIT(defaultPrice) } }));
                              }
                              setTimeout(() => e.target.select(), 0);
                            }}
                            onBlur={() => {
                              if (isSelected && customItems[prod.id]?.price) {
                                const num = parseFloat(String(customItems[prod.id].price).replace(",", "."));
                                if (!isNaN(num)) {
                                  const fixed = num.toFixed(2);
                                  updateCustomPrice(prod.id, fixed, formatPriceIT(fixed));
                                }
                              }
                            }}
                            className="w-[4.5rem] h-7 text-xs text-right font-mono bg-[#27272a] border-white/10 text-emerald-400 px-1.5 shrink-0"
                            data-testid={`custom-price-${prod.sku}`}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <ShareButtons
                      exportType="custom"
                      body={{
                        items: Object.entries(customItems).map(([product_id, data]) => ({ product_id, custom_price: parseFloat(data.price) || 0 })),
                        client_name: clientName,
                        include_images: customIncludeImages,
                      }}
                      disabled={customItemCount === 0}
                    />
                    <Button
                      data-testid="export-custom-btn"
                      onClick={() => {
                        const items = Object.entries(customItems).map(([product_id, data]) => ({
                          product_id,
                          custom_price: parseFloat(data.price) || 0,
                        }));
                        downloadPdf("/export/custom-price-pdf", {
                          items,
                          client_name: clientName,
                          include_images: customIncludeImages,
                        }, `listino_${clientName || "personalizzato"}_triolo.pdf`);
                      }}
                      disabled={exporting || customItemCount === 0}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8"
                    >
                      {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                      Scarica PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* TAB 1: Promozioni */}
            {activeTab === "promo" && (
              <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-3 sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-zinc-300">
                      Prodotti in promozione: <span className="text-amber-400 font-bold">{promoCount}</span>
                    </span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={promoIncludeImages} onCheckedChange={setPromoIncludeImages} data-testid="promo-include-images" className="w-3.5 h-3.5" />
                      <ImageIcon className="w-3 h-3 text-zinc-400" />
                      <span className="text-xs text-zinc-300">Foto</span>
                    </label>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      data-testid="export-promo-btn"
                      onClick={() => downloadPdf("/export/promo-pdf", { include_images: promoIncludeImages }, "promozioni_triolo.pdf")}
                      disabled={exporting || promoCount === 0}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs h-9 w-full sm:w-auto"
                    >
                      {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                      Scarica PDF Promozioni
                    </Button>
                    <div className="flex gap-2">
                      <ShareButtons exportType="promo" body={{ include_images: promoIncludeImages }} disabled={promoCount === 0} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* TAB 2: Per Categoria */}
            {activeTab === "category" && (
              <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-3 sm:p-5">
                  <div className="space-y-2 mb-3">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                      <Input
                        placeholder="Cerca categoria..."
                        value={catSearch}
                        onChange={(e) => setCatSearch(e.target.value)}
                        className="pl-8 h-8 text-xs bg-[#27272a] border-white/10 text-white"
                        data-testid="cat-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox checked={catIncludePrices} onCheckedChange={setCatIncludePrices} data-testid="cat-include-prices" className="w-3.5 h-3.5" />
                        <span className="text-xs text-zinc-300">Prezzi</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox checked={catIncludeImages} onCheckedChange={setCatIncludeImages} data-testid="cat-include-images" className="w-3.5 h-3.5" />
                        <ImageIcon className="w-3 h-3 text-zinc-400" />
                        <span className="text-xs text-zinc-300">Foto</span>
                      </label>
                      <button className="text-[10px] text-amber-400 underline ml-auto" onClick={() => setSelectedCats(filteredCats.map(c => c.id))}>Tutte</button>
                      <button className="text-[10px] text-zinc-400 underline" onClick={() => setSelectedCats([])}>Nessuna</button>
                    </div>
                  </div>

                  <div className="max-h-72 overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5">
                    {filteredCats.map(cat => (
                      <label key={cat.id} className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-white/5 cursor-pointer" data-testid={`cat-row-${cat.code}`}>
                        <Checkbox checked={selectedCats.includes(cat.id)} onCheckedChange={() => toggleCat(cat.id)} className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-mono text-amber-400 text-[10px] w-8">{cat.code}</span>
                        <span className="text-xs text-zinc-200 flex-1 truncate">{cat.name}</span>
                        <span className="text-[10px] text-zinc-500 shrink-0">{cat.product_count}</span>
                      </label>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <ShareButtons exportType="category" body={{ category_ids: selectedCats, include_prices: catIncludePrices, include_images: catIncludeImages }} disabled={selectedCats.length === 0} />
                    <Button
                      data-testid="export-category-btn"
                      onClick={() => downloadPdf("/export/category-pdf", { category_ids: selectedCats, include_prices: catIncludePrices, include_images: catIncludeImages }, "catalogo_categorie_triolo.pdf")}
                      disabled={exporting || selectedCats.length === 0}
                      className="bg-amber-500 hover:bg-amber-600 text-black text-xs h-8"
                    >
                      {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                      Scarica PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* TAB 3: Selezione Prodotti */}
            {activeTab === "selection" && (
              <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-3 sm:p-5">
                  <div className="space-y-2 mb-3">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                      <Input
                        placeholder="Cerca prodotto..."
                        value={prodSearch}
                        onChange={(e) => setProdSearch(e.target.value)}
                        className="pl-8 h-8 text-xs bg-[#27272a] border-white/10 text-white"
                        data-testid="prod-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox checked={selIncludePrices} onCheckedChange={setSelIncludePrices} data-testid="sel-include-prices" className="w-3.5 h-3.5" />
                        <span className="text-xs text-zinc-300">Prezzi</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox checked={selIncludeImages} onCheckedChange={setSelIncludeImages} data-testid="sel-include-images" className="w-3.5 h-3.5" />
                        <ImageIcon className="w-3 h-3 text-zinc-400" />
                        <span className="text-xs text-zinc-300">Foto</span>
                      </label>
                      <button className="text-[10px] text-emerald-400 underline ml-auto" onClick={() => setSelectedProds(filteredProds.map(p => p.id))}>Tutti</button>
                      <button className="text-[10px] text-zinc-400 underline" onClick={() => setSelectedProds([])}>Nessuno</button>
                    </div>
                  </div>

                  <div className="max-h-72 overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5">
                    {filteredProds.map(prod => (
                      <label key={prod.id} className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-white/5 cursor-pointer" data-testid={`prod-row-${prod.sku}`}>
                        <Checkbox checked={selectedProds.includes(prod.id)} onCheckedChange={() => toggleProd(prod.id)} className="w-3.5 h-3.5 shrink-0" />
                        <span className="text-xs text-zinc-200 flex-1 truncate">{prod.name}</span>
                        {prod.price_promo > 0 && <span className="text-[10px] bg-red-500/20 text-red-400 px-1 py-0.5 rounded shrink-0">PROMO</span>}
                      </label>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <ShareButtons exportType="selection" body={{ product_ids: selectedProds, include_prices: selIncludePrices, include_images: selIncludeImages }} disabled={selectedProds.length === 0} />
                    <Button
                      data-testid="export-selection-btn"
                      onClick={() => downloadPdf("/export/selection-pdf", { product_ids: selectedProds, include_prices: selIncludePrices, include_images: selIncludeImages }, "selezione_triolo.pdf")}
                      disabled={exporting || selectedProds.length === 0}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8"
                    >
                      {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                      Scarica PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
